import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMapPin, FiTool, FiLayers, FiZap, FiArrowRight } = FiIcons;

const MineSection = ({ nextStep, t }) => {
  const [selectedArea, setSelectedArea] = useState(null);

  const miningAreas = [
    {
      id: 'extraction',
      title: t('extraction'),
      icon: FiTool,
      description: t('extractionDesc'),
      details: t('extractionDetails')
    },
    {
      id: 'processing',
      title: t('processing'),
      icon: FiLayers,
      description: t('processingDesc'),
      details: t('processingDetails')
    },
    {
      id: 'recovery',
      title: t('recovery'),
      icon: FiZap,
      description: t('recoveryDesc'),
      details: t('recoveryDetails')
    }
  ];

  const earthLayers = [
    { name: t('surfaceLayer'), depth: '0 km', color: 'bg-green-600/30' },
    { name: t('continentalCrust'), depth: '0-35 km', color: 'bg-yellow-600/30' },
    { name: t('upperMantle'), depth: '35-660 km', color: 'bg-orange-600/30' },
    { name: t('lowerMantle'), depth: '660-2900 km', color: 'bg-red-600/30' },
    { name: t('coreMantle'), depth: '2900-3000 km', color: 'bg-red-800/30' }
  ];

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-amber-400 to-pantone-blue bg-clip-text text-transparent">
            {t('mineTitle')}
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            {t('mineDescription')}
          </p>
        </motion.div>

        {/* Mining Visual */}
        <div className="relative mb-16">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative bg-gradient-to-b from-pantone-blue/20 to-blue-900/20 rounded-3xl p-8 backdrop-blur-sm border border-pantone-blue/20"
          >
            {/* Earth Layers Illustration */}
            <div className="relative h-96 mb-8 overflow-hidden rounded-2xl">
              <div className="absolute inset-0 bg-gradient-to-b from-blue-600/30 to-red-800/50"></div>
              
              {/* Earth Layers */}
              <div className="absolute inset-0">
                {earthLayers.map((layer, i) => (
                  <motion.div
                    key={i}
                    initial={{ x: -100, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: i * 0.2 }}
                    className={`absolute left-0 right-0 h-16 border-b border-white/20 ${layer.color}`}
                    style={{ top: `${i * 20}%` }}
                  >
                    <div className="flex items-center justify-between px-4 h-full">
                      <div className="text-left">
                        <div className="text-sm font-semibold text-white">{layer.name}</div>
                        <div className="text-xs text-gray-300">{layer.depth}</div>
                      </div>
                      {i === 4 && (
                        <motion.div
                          animate={{ scale: [1, 1.2, 1] }}
                          transition={{ duration: 2, repeat: Infinity }}
                          className="w-3 h-3 bg-white rounded-full shadow-lg"
                          title="Diamond Formation Zone"
                        />
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Interactive Mining Process */}
            <div className="grid md:grid-cols-3 gap-6">
              {miningAreas.map((area, index) => (
                <motion.div
                  key={area.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.2 }}
                  onHoverStart={() => setSelectedArea(area.id)}
                  onHoverEnd={() => setSelectedArea(null)}
                  className="group cursor-pointer"
                >
                  <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-pantone-blue/10 transition-all duration-300 h-full">
                    <div className="flex items-center mb-4">
                      <div className="bg-pantone-blue/20 p-3 rounded-lg mr-4">
                        <SafeIcon icon={area.icon} className="w-6 h-6 text-pantone-blue/80" />
                      </div>
                      <h3 className="text-xl font-semibold text-white">{area.title}</h3>
                    </div>
                    
                    <p className="text-gray-300 mb-4">{area.description}</p>
                    
                    <AnimatePresence>
                      {selectedArea === area.id && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="text-sm text-pantone-blue/80 bg-pantone-blue/10 p-3 rounded-lg"
                        >
                          {area.details}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Fun Fact */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="text-center mb-12"
        >
          <div className="bg-gradient-to-r from-pantone-blue/20 to-blue-600/20 backdrop-blur-sm border border-pantone-blue/20 rounded-2xl p-8 max-w-4xl mx-auto">
            <SafeIcon icon={FiMapPin} className="w-12 h-12 text-pantone-blue/80 mx-auto mb-4" />
            <h3 className="text-2xl font-semibold mb-4 text-pantone-blue/80">{t('didYouKnow')}</h3>
            <p className="text-lg text-gray-300">
              {t('diamondFormation')}
            </p>
          </div>
        </motion.div>

        {/* Continue Button */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="text-center"
        >
          <button
            onClick={nextStep}
            className="group bg-gradient-to-r from-pantone-blue to-blue-700 hover:from-blue-700 hover:to-pantone-blue text-white px-8 py-4 rounded-full text-lg font-semibold shadow-xl transition-all duration-300 flex items-center gap-3 mx-auto"
          >
            {t('nextSorting')}
            <SafeIcon icon={FiArrowRight} className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default MineSection;